import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CopilotPageContext = createContext({
  entityContext: null,
  setEntityContext: () => {},
  clearEntityContext: () => {},
});

function normalizeText(value, maxLength = 180) {
  return String(value || '').trim().slice(0, maxLength);
}

function normalizeEntityContext(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const nextValue = {
    entityType: normalizeText(value.entityType, 60),
    entityId: normalizeText(value.entityId, 120),
    label: normalizeText(value.label, 180),
    customerId: normalizeText(value.customerId, 120),
    invoiceId: normalizeText(value.invoiceId, 120),
    invoiceNumber: normalizeText(value.invoiceNumber, 120),
    customerName: normalizeText(value.customerName, 180),
    status: normalizeText(value.status, 80),
    email: normalizeText(value.email, 180),
    documentType: normalizeText(value.documentType, 40),
  };

  return Object.values(nextValue).some(Boolean) ? nextValue : null;
}

export function CopilotPageContextProvider({ children }) {
  const [entityContext, setEntityContextState] = useState(null);
  const setEntityContext = useCallback((nextValue) => {
    setEntityContextState(normalizeEntityContext(nextValue));
  }, []);
  const clearEntityContext = useCallback(() => {
    setEntityContextState(null);
  }, []);

  const value = useMemo(
    () => ({
      entityContext,
      setEntityContext,
      clearEntityContext,
    }),
    [clearEntityContext, entityContext, setEntityContext]
  );

  return <CopilotPageContext.Provider value={value}>{children}</CopilotPageContext.Provider>;
}

export function useCopilotPageContext() {
  return useContext(CopilotPageContext);
}

export function usePublishCopilotPageContext(entityContext) {
  const { setEntityContext, clearEntityContext } = useCopilotPageContext();

  useEffect(() => {
    if (entityContext) {
      setEntityContext(entityContext);
      return () => {
        clearEntityContext();
      };
    }

    clearEntityContext();
    return undefined;
  }, [clearEntityContext, entityContext, setEntityContext]);
}

export function __normalizeCopilotEntityContext(value) {
  return normalizeEntityContext(value);
}
